import { Link as RouterLink } from "react-router-dom";
import { withFacilityDiscoveryReturn } from "../../../common/facilityNavigation";
import { Box, Button, ListItem, Stack, Typography } from "@mui/material";
import { formatMoney } from "@bw/shared/utils";
import type {
  PublicProgramTrackResponse,
  ProgramTrackOptionResponse,
  FacilityPromotionSummary,
} from "@bw/shared/types/generated";
import { Fragment, type ReactNode } from "react";
import { titleCase, spotsLabel, ageBandLabel } from "./classDisplay";
import { PromotionSummaryCard } from "../../../common/components/PromotionSummaryCard";

type Track = ProgramTrackOptionResponse | PublicProgramTrackResponse;

/** The track's enroll route (also used by the class detail's sticky bar). */
export function trackEnrollPath(
  facilityId: string,
  programId: string,
  trackId: string,
  facilitySearchReturn?: string,
): string {
  return withFacilityDiscoveryReturn(
    `/classes/${facilityId}/${programId}/enroll/${trackId}`,
    facilitySearchReturn,
  );
}

/** The one-line facts for a track: price · level · ages · spots, each its own span. */
export function trackFacts(track: Track): ReactNode[] {
  const skillLabel =
    track.skill_level === "all" ? "All levels" : titleCase(track.skill_level);
  const facts: ReactNode[] = [
    <Box component="span" key="price" sx={{ fontWeight: 700, color: "text.primary" }}>
      {track.base_price != null ? formatMoney(track.base_price, track.currency) : "—"}
    </Box>,
  ];
  if (skillLabel && track.name.trim().toLowerCase() !== skillLabel.toLowerCase()) {
    facts.push(<span key="level">{skillLabel}</span>);
  }
  const ageBand = ageBandLabel(track.age_min, track.age_max);
  if (ageBand) facts.push(<span key="age">{ageBand}</span>);
  if (track.status !== "active") {
    facts.push(<span key="closed">Enrollment closed</span>);
  } else if (track.available_spots > 0) {
    facts.push(<span key="spots">{spotsLabel(track.available_spots)}</span>);
  }
  return facts;
}

/**
 * The class-detail facts for its single option: "price · level · N sessions ·
 * spots". The session count only when the whole schedule is known (never a
 * partial page's count).
 */
export function trackSummaryFacts(track: Track, sessionCount: number | null): ReactNode[] {
  const skillLabel =
    track.skill_level === "all" ? "All levels" : titleCase(track.skill_level);
  const facts: ReactNode[] = [
    <Box component="span" key="price" sx={{ fontWeight: 700, color: "text.primary" }}>
      {track.base_price != null ? formatMoney(track.base_price, track.currency) : "—"}
    </Box>,
  ];
  if (skillLabel && track.name.trim().toLowerCase() !== skillLabel.toLowerCase()) {
    facts.push(<span key="level">{skillLabel}</span>);
  }
  const ageBand = ageBandLabel(track.age_min, track.age_max);
  if (ageBand) facts.push(<span key="age">{ageBand}</span>);
  if (sessionCount != null && sessionCount > 0) {
    facts.push(<span key="sessions">{`${sessionCount} ${sessionCount === 1 ? "session" : "sessions"}`}</span>);
  }
  if (track.status !== "active") {
    facts.push(<span key="closed">Enrollment closed</span>);
  } else if (track.available_spots > 0) {
    facts.push(<span key="spots">{spotsLabel(track.available_spots)}</span>);
  } else {
    facts.push(<span key="full">Waitlist</span>);
  }
  return facts;
}

export function joinFacts(facts: ReactNode[]): ReactNode {
  return facts.map((fact, index) => (
    <Fragment key={index}>
      {index > 0 ? (
        <Box component="span" aria-hidden sx={{ mx: 0.5 }}>
          ·
        </Box>
      ) : null}
      {fact}
    </Fragment>
  ));
}

/**
 * The enroll action for a track: lime "Enroll" while spots remain, an outlined
 * "Waitlist" when the backend reports none left (the backend owns the
 * enrolled-vs-waitlisted decision, so both route to the same enroll surface),
 * nothing once enrollment is closed.
 */
export function TrackAction({
  facilityId,
  programId,
  track,
  facilitySearchReturn,
}: {
  facilityId: string;
  programId: string;
  track: Track;
  facilitySearchReturn?: string;
}) {
  if (track.status !== "active") return null;
  const full = track.available_spots <= 0;
  return (
    <Button
      component={RouterLink}
      to={trackEnrollPath(facilityId, programId, track.track_id, facilitySearchReturn)}
      variant={full ? "outlined" : "contained"}
      size="small"
      data-full={full || undefined}
      sx={{ borderRadius: 999, px: 1.75, minWidth: 0, flexShrink: 0 }}
    >
      {full ? "Waitlist" : "Enroll"}
    </Button>
  );
}

/**
 * One buyable TRACK row: name, then the backend's facts verbatim (fee via the
 * money util, skill level, age band, spots left) and its one action. An absent
 * price renders "—"; inactive raw pricing facts never replace that field.
 */
export function TrackRow({
  facilityId,
  programId,
  track,
  facilitySearchReturn,
  promotion,
  hideAction = false,
}: {
  facilityId: string;
  programId: string;
  track: Track;
  facilitySearchReturn?: string;
  promotion?: FacilityPromotionSummary;
  /** The detail page's sticky bar carries the action for a single option. */
  hideAction?: boolean;
}) {
  return (
    <ListItem
      component="li"
      data-testid="track-row"
      disablePadding
      sx={{ px: { xs: 0.5, sm: 1 }, py: 0.75 }}
    >
      <Stack direction="row" spacing={1.5} sx={{ width: "100%", alignItems: "center" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, overflowWrap: "anywhere" }}>
            {track.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {joinFacts(trackFacts(track))}
          </Typography>
          {promotion ? (
            <Box sx={{ mt: 1 }}>
              <PromotionSummaryCard promotion={promotion} compact />
            </Box>
          ) : null}
        </Box>
        {hideAction ? null : (
          <TrackAction
            facilityId={facilityId}
            programId={programId}
            track={track}
            facilitySearchReturn={facilitySearchReturn}
          />
        )}
      </Stack>
    </ListItem>
  );
}
